import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';

import { CommonService } from '@app/common/common.service';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { SpinnerService } from '@app/shared/spinner.service';
import { parseTz } from '@app/shared/time';
import { Station } from '@app/shared/_models/common';
import { Bank, BanksList, CashTransfer, CashTransferInput, NO_LINKED_BANK_MSG, SAFE_RESERVED_HINT, StationBalance, bankChoices, defaultBank, isForbidden, isPosStation, isSafeStation, lostCause, lostReply, positiveCents, refusalText } from '../reglement.models';
import { ReglementService } from '../reglement.service';

// free = mouvement libre (Admin) ; deposit = dépôt en banque depuis un coffre, refill = coffre → caisse du même magasin (Admin ou habilitation Coffres-forts).
export type TransferMode = 'free' | 'deposit' | 'refill';

export interface TransferDialogData {
	mode?: TransferMode;
	store?: number | null;		// son coffre est la source proposée
	source?: number | null;		// source imposée (bouton d'un coffre de la carte « Coffres-forts »)
	destination?: number | null;	// caisse proposée (approvisionnement depuis une caisse : la sienne)
}

export interface TransferDialogResult {
	msg: string;
	transfer: CashTransfer;
}

type Target = 'station' | 'bank';

export const NO_BANK_MSG = 'Aucune banque : cochez le rôle Banque sur la fiche du tiers.';

@Component({
	selector: 'app-transfer-dialog',
	standalone: true,
	templateUrl: './transfer-dialog.component.html',
	styleUrls: ['./transfer-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatTooltipModule, FormatCurrencyPipe],
})
export class TransferDialogComponent implements OnInit {

	private reglementService = inject(ReglementService);
	private commonService = inject(CommonService);
	private spin = inject(SpinnerService);
	private destroyRef = inject(DestroyRef);
	public dialogRef = inject(MatDialogRef<TransferDialogComponent, TransferDialogResult>);
	private fc = new FormatCurrencyPipe();
	public data: TransferDialogData = inject(MAT_DIALOG_DATA, { optional: true }) ?? {};
	public readonly deposit = this.data.mode === 'deposit';
	public readonly refill = this.data.mode === 'refill';
	readonly reservedHint = SAFE_RESERVED_HINT;

	public stations: Station[] = [];
	private storeNames = new Map<number, string>();
	private bankList: BanksList | null = null;
	// null tant que la liste n'est pas lue (ou illisible) : « aucune banque » ne se dit que d'une liste lue et vide.
	// Dépôt : banques liées au magasin du coffre source ; mouvement libre : toutes les banques.
	public banks: Bank[] | null = null;
	public defaultBankId: number | null = null;
	public banksError = '';
	public loadError = '';
	public sendError = '';
	public sending = false;
	// Réponse perdue : mouvement peut-être enregistré ; la boîte ne renvoie plus rien (le 4601 n'a pas de garde de doublon).
	public lost = false;
	public form = new FormGroup({
		source: new FormControl<number | null>(null, Validators.required),
		target: new FormControl<Target>(this.deposit ? 'bank' : 'station', { nonNullable: true }),
		destination: new FormControl<number | null>(null),
		bank: new FormControl<number | null>(null),
		amount: new FormControl<number | null>(null, [Validators.required, positiveCents]),
	});
	// Espèces attendues du poste source, relues à chaque choix de source (sans voile : la saisie continue).
	public sourceBalance: StationBalance | null = null;
	public sourceBalanceError = '';
	public sourceBalanceLoading = false;
	// Coffre lu sans l'habilitation Coffres-forts : le serveur refuse (403), on affiche « — », le mouvement reste possible.
	public sourceBalanceReserved = false;
	// Dépôt enregistré : la boîte reste ouverte sur le récapitulatif et le bordereau de versement.
	public done: TransferDialogResult | null = null;
	public printError = '';

	ngOnInit(): void {
		// Dépôt : la banque proposée suit le magasin du coffre choisi.
		this.form.controls.source.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshBanks(true));
		// Approvisionnement : la caisse choisie reste du magasin du coffre, sinon le serveur refuse (403).
		if (this.refill) this.form.controls.source.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			if (!this.destinations.some((st) => st.id === this.form.controls.destination.value)) this.form.controls.destination.setValue(null);
		});
		// switchMap : la réponse d'une source abandonnée n'écrase jamais celle de la source choisie.
		this.form.controls.source.valueChanges.pipe(
			distinctUntilChanged(),
			tap((id) => { this.sourceBalance = null; this.sourceBalanceError = ''; this.sourceBalanceReserved = false; this.sourceBalanceLoading = id !== null; }),
			switchMap((id) => id === null ? EMPTY : this.reglementService.getBalance$(id).pipe(
				map((b) => ({ b, e: '', reserved: false })),
				catchError((e) => of({ b: null, e: refusalText(e), reserved: isForbidden(e) && isSafeStation(this.stations.find((st) => st.id === id)) })),
			)),
			takeUntilDestroyed(this.destroyRef),
		).subscribe(({ b, e, reserved }) => {
			this.sourceBalanceLoading = false;
			this.sourceBalance = b;
			this.sourceBalanceReserved = reserved;
			this.sourceBalanceError = reserved ? '' : e;
		});
		// Banques lues à l'ouverture (liste courte, pas de recherche) ; leur échec n'empêche pas un mouvement entre postes.
		// Approvisionnement : aucune banque en jeu, la liste n'est pas lue.
		const banks$: Observable<{ banks: BanksList | null; error: string }> = this.refill ? of({ banks: null, error: '' }) : this.reglementService.getBanks$().pipe(
			map((banks) => ({ banks, error: '' })),
			catchError((e) => of({ banks: null, error: refusalText(e) })),
		);
		forkJoin({ stations: this.commonService.getStations(), stores: this.commonService.getStores(), banks: banks$ })
			.pipe(this.spin.block(this.refill ? 'Chargement des postes…' : 'Chargement des postes et des banques…'), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ({ stations, stores, banks }) => {
					this.bankList = banks.banks;
					this.banksError = banks.error;
					(stores ?? []).forEach((s) => { if (s.id !== undefined) this.storeNames.set(s.id, s.name ?? ''); });
					// GET /common/stations ne dit pas si un poste est actif : un poste inactif est proposé, le serveur le refuse (404).
					// Caisses et coffres seulement : un poste sans tiroir-caisse ne verse ni ne reçoit d'espèces (403 du serveur, JP 04/10).
					this.stations = (stations ?? []).filter((st) => st.id !== undefined && (isPosStation(st) || isSafeStation(st)))
						.sort((a, b) => (a.store ?? 0) - (b.store ?? 0) || Number(isSafeStation(b)) - Number(isSafeStation(a)) || a.id! - b.id!);
					const initial = this.sources.find((st) => st.id === this.data.source) ?? this.sources.find((st) => isSafeStation(st) && st.store === this.data.store);
					if (initial && this.form.controls.source.value === null) this.form.controls.source.setValue(initial.id!);
					else this.refreshBanks(true);
					const destination = this.refill ? this.destinations.find((st) => st.id === this.data.destination) : undefined;
					if (destination && this.form.controls.destination.value === null) this.form.controls.destination.setValue(destination.id!);
				},
				error: (e) => this.loadError = refusalText(e),
			});
	}

	// Dépôt : banques liées au magasin du coffre, défaut présélectionné (preselect) ; mouvement libre : toutes, sans présélection.
	private refreshBanks(preselect: boolean): void {
		const list = this.bankList;
		if (!list) { this.banks = null; this.defaultBankId = null; return; }
		if (!this.deposit) { this.banks = list.banks; this.defaultBankId = null; return; }
		const store = this.sourceStore;
		// Sans coffre choisi, rien à proposer ni à dire : « aucune banque liée » ne se dit que d'un magasin connu.
		this.banks = store === null ? null : bankChoices(list.stores, list.banks, store);
		this.defaultBankId = store === null ? null : defaultBank(list.stores, store);
		if (preselect) this.form.controls.bank.setValue(this.defaultBankId);
	}

	private get sourceStore(): number | null {
		return this.stations.find((st) => st.id === this.form.controls.source.value)?.store ?? null;
	}

	get noBankMsg(): string {
		return this.deposit ? NO_LINKED_BANK_MSG : NO_BANK_MSG;
	}

	bankLabel(b: Bank): string {
		return (b.name ?? 'Banque ' + b.id) + (b.id === this.defaultBankId ? ' (par défaut)' : '');
	}

	// Un dépôt ou un approvisionnement part toujours d'un coffre : c'est ce que l'habilitation Coffres-forts couvre côté serveur.
	get sources(): Station[] {
		return this.deposit || this.refill ? this.stations.filter((st) => isSafeStation(st)) : this.stations;
	}

	label(st: Station): string {
		const store = st.store !== undefined ? this.storeNames.get(st.store) : undefined;
		return (st.name ?? '#' + st.id) + (store ? ' — ' + store : '');
	}

	get destinations(): Station[] {
		const source = this.form.controls.source.value;
		if (!this.refill) return this.stations.filter((st) => st.id !== source);
		const store = this.sourceStore;
		return store === null ? [] : this.stations.filter((st) => isPosStation(st) && st.store === store);
	}

	setTarget(t: Target): void {
		if (this.deposit || this.refill) return;
		this.form.controls.target.setValue(t);
		this.form.markAsDirty();
		if (!this.lost) this.sendError = '';
	}

	// Exactement une cible : le serveur refuse (400) ni l'une ni l'autre, ou les deux.
	get targetError(): string {
		const v = this.form.getRawValue();
		if (v.target === 'bank') {
			if (this.banks?.length === 0) return this.noBankMsg;
			return v.bank ? '' : 'Choisissez la banque qui reçoit.';
		}
		if (this.refill && v.source !== null && !this.destinations.length) return 'Aucune caisse dans le magasin de ce coffre-fort.';
		if (!v.destination) return this.refill ? 'Choisissez la caisse qui reçoit.' : 'Choisissez le poste qui reçoit.';
		if (v.destination === v.source) return 'Le poste qui reçoit doit différer de celui d\'où sort l\'argent.';
		return '';
	}

	// Avertir sans bloquer, comme le versement au coffre : le solde est théorique.
	get overBalance(): boolean {
		const amount = this.form.controls.amount;
		return !!this.sourceBalance && amount.valid && Number(amount.value) > this.sourceBalance.balance;
	}

	get canConfirm(): boolean {
		return this.form.valid && !this.targetError && !this.sending && !this.done && !this.lost;
	}

	input(): CashTransferInput {
		const v = this.form.getRawValue();
		return {
			source: v.source!,
			destination: v.target === 'station' ? v.destination : null,
			partner: v.target === 'bank' ? v.bank : null,
			amount: Number(v.amount),
		};
	}

	confirm(): void {
		if (!this.canConfirm || this.spin.isLoading) return;
		this.sending = true;
		this.sendError = '';
		this.dialogRef.disableClose = true;	// le voile arrête la souris, pas Échap : fermer couperait le POST et cacherait un mouvement peut-être enregistré
		const input = this.input();
		const source = this.stations.find((st) => st.id === input.source);
		this.reglementService.postTransfer$(input)
			.pipe(this.spin.block(this.deposit ? 'Enregistrement du dépôt…' : 'Enregistrement du mouvement…'), finalize(() => { this.sending = false; this.dialogRef.disableClose = this.lost; }), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (r) => {
					const result = { msg: r.msg || (this.deposit ? 'Dépôt enregistré.' : 'Mouvement enregistré.'), transfer: r.data };
					if (this.deposit) this.done = result;
					else this.dialogRef.close(result);
				},
				error: (e) => {
					this.lost = lostReply(e);
					this.sendError = this.lost
						? `Réponse perdue (${lostCause(e)}) : ${this.deposit ? 'le dépôt' : 'le mouvement'} de ${this.fc.transform(input.amount)} € depuis « ${source ? this.label(source) : 'poste ' + input.source} » a peut-être été enregistré. Fermez, puis vérifiez la carte Mouvements d'aujourd'hui (magasin de ce poste, tous postes) avant de le refaire.`
						: refusalText(e);
				},
			});
	}

	// Appelé dans le clic : le service y ouvre la fenêtre avant de lire le bordereau.
	printDeposit(): void {
		if (!this.done) return;
		this.printError = '';
		this.reglementService.printDeposit$(this.done.transfer.id).pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({ error: (e) => this.printError = refusalText(e) });
	}

	get doneStoreName(): string {
		const store = this.done?.transfer.source_store ?? null;
		return store === null ? '' : this.storeNames.get(store) || 'Magasin ' + store;
	}

	stamp(dat: string | null | undefined): string {
		return parseTz(dat)?.format('DD/MM/YYYY HH:mm') ?? '';
	}

	// Après un dépôt, fermer rend ce que la boîte rendait avant le récapitulatif : la phrase du serveur et la ligne créée.
	// Jamais pendant l'envoi, en doublure du [disabled] des boutons : fermer détruirait la boîte et couperait le POST, mouvement peut-être enregistré.
	cancel(): void {
		if (this.sending) return;
		if (this.lost) this.reglementService.markUncertain('transfer');
		this.dialogRef.close(this.done ?? undefined);
	}
}
